import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getValidToken, handleAuthSuccess } from '../../utils/auth';
import { useView } from '../../context/ViewContext';

const BusinessJoinPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setCurrentView } = useView();
  const token = params.get('token') || '';
  const isSignedIn = Boolean(getValidToken());
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    let active = true;
    axios.post('/api/team/invites/preview', { token })
      .then((response) => {
        if (active) setInvitation(response.data?.invitation || null);
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.error || 'Unable to load this invitation.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [token]);

  const accept = async () => {
    setError('');
    setAccepting(true);
    try {
      const response = await axios.post('/api/team/invites/accept', { token });
      const { token: refreshedToken, user, workspaceLandingPath } = response.data || {};
      if (!refreshedToken) throw new Error('We couldn’t open your workspace. Please try again.');
      setMessage('Invitation accepted. Opening your workspace…');
      handleAuthSuccess({
        token: refreshedToken,
        user,
        navigate,
        setCurrentView,
        redirectPath: workspaceLandingPath || user?.workspaceLandingPath || '/team',
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Unable to accept invitation.');
    } finally {
      setAccepting(false);
    }
  };

  const decline = async () => {
    setError('');
    setDeclining(true);
    try {
      await axios.post('/api/team/invites/decline', { token });
      setInvitation(null);
      setMessage('Invitation declined. The business team has been notified.');
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.error || err.message || 'Unable to decline invitation.');
    } finally {
      setDeclining(false);
    }
  };

  return (
    <main style={{ maxWidth: 640, margin: '0 auto', padding: 32 }}>
      <p className="eyebrow">Team invitation</p>
      <h1>{invitation ? `Join ${invitation.businessName}` : 'Review invitation'}</h1>
      {!token && <p>This invitation link is invalid or incomplete. Ask the business owner to send a new one.</p>}
      {loading && <p>Loading invitation…</p>}
      {invitation && (
        <section aria-label="Invitation details">
          <p><strong>Business:</strong> {invitation.businessName}</p>
          <p><strong>Invited email:</strong> {invitation.email}</p>
          <p><strong>Role:</strong> {String(invitation.role || '').replace('_', ' ')}</p>
        </section>
      )}
      {invitation && !isSignedIn && <p>Please <Link to={`/login?next=${encodeURIComponent(`/join/business?token=${token}`)}`}>log in</Link> or <Link to={`/signup?next=${encodeURIComponent(`/join/business?token=${token}`)}`}>create an account</Link> with the invited email address. You will return here to confirm before joining.</p>}
      {invitation && isSignedIn && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <button type="button" onClick={accept} disabled={accepting || declining}>{accepting ? 'Joining…' : `Accept and join ${invitation.businessName}`}</button>
          <button type="button" onClick={decline} disabled={accepting || declining}>{declining ? 'Declining…' : 'Decline invitation'}</button>
        </div>
      )}
      {message && <p>{message}</p>}
      {error && <p role="alert" style={{ color: '#991b1b' }}>{error}</p>}
    </main>
  );
};

export default BusinessJoinPage;
